/**
 * API Loading State Manager
 * 
 * Provides a centralized way to manage loading states for API calls
 * to prevent UI flickering and improve user experience.
 */

// Store loading states by operation key
const loadingStates = new Map();
const subscribers = new Map();

/**
 * Set the loading state for an operation
 * @param {string} key - Unique key for the operation
 * @param {boolean} isLoading - Whether the operation is loading
 */
export const setLoadingState = (key, isLoading) => {
  loadingStates.set(key, isLoading);
  
  // Notify all subscribers for this key
  if (subscribers.has(key)) {
    subscribers.get(key).forEach(callback => {
      callback(isLoading);
    });
  }
};

/**
 * Get the current loading state for an operation
 * @param {string} key - Key for the operation
 * @returns {boolean} - Whether the operation is loading
 */
export const getLoadingState = (key) => {
  return loadingStates.get(key) || false;
};

/**
 * Subscribe to changes in loading state for an operation
 * @param {string} key - Key for the operation
 * @param {Function} callback - Function to call when loading state changes
 * @returns {Function} - Function to unsubscribe
 */
export const subscribeToLoadingState = (key, callback) => {
  if (!subscribers.has(key)) {
    subscribers.set(key, new Set());
  }
  
  subscribers.get(key).add(callback);
  
  // Return unsubscribe function
  return () => {
    const keySubscribers = subscribers.get(key);
    if (keySubscribers) {
      keySubscribers.delete(callback);
      if (keySubscribers.size === 0) {
        subscribers.delete(key);
      }
    }
  };
};

/**
 * Wrap an API call with loading state management
 * @param {string} key - Unique key for the operation
 * @param {Function} apiCall - Function that returns a Promise
 * @returns {Promise} - The original Promise with loading state management
 */
export const withLoadingState = async (key, apiCall) => {
  try {
    setLoadingState(key, true);
    const result = await apiCall();
    return result;
  } finally {
    setLoadingState(key, false);
  }
};

export default {
  setLoadingState,
  getLoadingState,
  subscribeToLoadingState,
  withLoadingState
};